import React, { useState } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import {
  Bevel,
  Button,
  RunningMan,
  Text,
  TextField,
  Window,
  isDesktop,
  makeStyles,
  useTheme,
} from '@ogaim/ui';
import { signUp } from '@ogaim/core';
import { useStore } from '../store';

/** Screen-name rules, mirrored from the pre-signup Lambda so the user finds out here. */
const SCREEN_NAME_RE = /^[A-Za-z][A-Za-z0-9 ]{2,15}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function SignUpScreen({ frame = true }: { frame?: boolean } = {}) {
  const styles = useStyles();
  const goTo = useStore((s) => s.goTo);
  const signIn = useStore((s) => s.signIn);

  const [inviteCode, setInviteCode] = useState('');
  const [screenName, setScreenName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const problem = validate({ inviteCode, screenName, email, password, confirm });

  const submit = async () => {
    if (problem || busy) return;
    setBusy(true);
    setError(null);
    try {
      await signUp(screenName.trim(), password, email.trim(), inviteCode);
      // The backend auto-confirms invited users, so drop them straight in.
      await signIn(screenName.trim(), password);
    } catch (err: any) {
      setError(err?.message ?? 'Could not create your screen name.');
      setBusy(false);
    }
  };

  const body = (
    <ScrollView contentContainerStyle={styles.body} keyboardShouldPersistTaps="handled">
          <Bevel variant="sunken" background="#FFFFCC" contentStyle={styles.noticeInner}>
            <Text style={styles.notice}>
              OG Instant Messenger is invitation only. Enter the code you were given to
              claim a screen name.
            </Text>
          </Bevel>

          <Field label="Invitation Code">
            <TextField
              value={inviteCode}
              onChangeText={(t) => setInviteCode(t.toUpperCase())}
              editable={!busy}
              autoCapitalize="characters"
              placeholder="XXXX-XXXX"
              maxLength={9}
              accessibilityLabel="Invitation Code"
            />
          </Field>

          <Field label="Screen Name" hint="3–16 characters, starts with a letter.">
            <TextField
              value={screenName}
              onChangeText={setScreenName}
              editable={!busy}
              maxLength={16}
              accessibilityLabel="Screen Name"
            />
          </Field>

          <Field label="Email" hint="Used only for password recovery.">
            <TextField
              value={email}
              onChangeText={setEmail}
              editable={!busy}
              keyboardType="email-address"
              textContentType="emailAddress"
              accessibilityLabel="Email"
            />
          </Field>

          <Field label="Password" hint="At least 10 characters, with a lowercase letter and a number.">
            <TextField
              value={password}
              onChangeText={setPassword}
              editable={!busy}
              secureTextEntry
              textContentType="newPassword"
              accessibilityLabel="Password"
            />
          </Field>

          <Field label="Confirm Password">
            <TextField
              value={confirm}
              onChangeText={setConfirm}
              editable={!busy}
              secureTextEntry
              onSubmitEditing={submit}
              accessibilityLabel="Confirm Password"
            />
          </Field>

          <View style={styles.statusWell}>
            {error ? (
              <Text style={styles.errorText}>{error}</Text>
            ) : problem && anyTouched({ inviteCode, screenName, email, password, confirm }) ? (
              <Text style={styles.problemText}>{problem}</Text>
            ) : null}
          </View>

          <View style={styles.actionRow}>
            <Button title="Cancel" onPress={() => goTo('signon')} disabled={busy} style={styles.action} />
            <Button
              title={busy ? 'Creating…' : 'Create'}
              onPress={submit}
              disabled={!!problem || busy}
              primary
              style={styles.action}
            />
          </View>
    </ScrollView>
  );

  if (!frame) return body;

  return (
    <View style={styles.backdrop}>
      <Window
        title="Get a Screen Name"
        style={styles.window}
        icon={<RunningMan size={14} />}
        onClose={busy ? undefined : () => goTo('signon')}
      >
        {body}
      </Window>
    </View>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  const styles = useStyles();
  return (
    <View style={styles.field}>
      <Text style={styles.fieldLabel}>{label}</Text>
      {children}
      {hint ? (
        <Text disabled style={styles.hint}>
          {hint}
        </Text>
      ) : null}
    </View>
  );
}

interface Form {
  inviteCode: string;
  screenName: string;
  email: string;
  password: string;
  confirm: string;
}

const anyTouched = (f: Form) => Object.values(f).some((v) => v.length > 0);

/**
 * Returns the first problem, or null when the form is ready to submit.
 *
 * These rules must mirror the Cognito password policy in infra exactly. They
 * drifted once: the form accepted a password with no lowercase letter, Cognito
 * rejected it, and because the policy is enforced *before* the pre-signup
 * trigger runs there was no Lambda log to explain why. Anything this function
 * lets through must be something the service will actually accept.
 */
function validate(f: Form): string | null {
  if (!f.inviteCode.trim()) return 'An invitation code is required.';
  if (!SCREEN_NAME_RE.test(f.screenName.trim()))
    return 'Screen names are 3–16 characters, start with a letter, and use only letters, numbers, and spaces.';
  if (!EMAIL_RE.test(f.email.trim())) return 'Enter a valid email address.';
  if (f.password.length < 10) return 'Passwords must be at least 10 characters.';
  if (!/[a-z]/.test(f.password)) return 'Passwords must include a lowercase letter.';
  if (!/\d/.test(f.password)) return 'Passwords must include a number.';
  if (f.password !== f.confirm) return 'The passwords do not match.';
  return null;
}

const useStyles = makeStyles((t) => ({
  backdrop: { flex: 1, backgroundColor: t.colors.desktop, alignItems: 'center', justifyContent: 'center', padding: 12 },
  window: { width: '100%', maxWidth: 340, flex: isDesktop ? 0 : 1, maxHeight: 620 },
  body: { padding: 10, gap: 8 },
  noticeInner: { padding: 6 },
  notice: { lineHeight: 15 },
  field: { gap: 2 },
  fieldLabel: {},
  hint: { fontSize: 10 },
  statusWell: { minHeight: 30, justifyContent: 'center' },
  errorText: { color: '#800000' },
  // Not `disabled` grey: this is why the button will not submit.
  problemText: { color: '#800000' },
  actionRow: { flexDirection: 'row', gap: 6, marginTop: 2 },
  action: { flex: 1 },
}));
